import React from "react";
import { motion } from "framer-motion";

/**
 * Section
 * Standard wrapper used by every portfolio section.
 * - Provides the consistent container + vertical rhythm.
 * - Renders an optional mono label and section heading.
 * - Animates content in on scroll (subtle fade + rise).
 */
export const Section = ({
  id,
  label,
  title,
  index,
  children,
  className = "",
  containerClassName = "",
}) => {
  return (
    <section
      id={id}
      data-testid={id ? `section-${id}` : undefined}
      className={`relative w-full py-24 md:py-32 ${className}`}
    >
      <div className={`max-w-7xl mx-auto px-6 md:px-12 ${containerClassName}`}>
        {(label || title) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mb-12 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
          >
            <div>
              {label && (
                <div className="flex items-center gap-3 mb-5">
                  {typeof index !== "undefined" && (
                    <span className="text-mono-label">
                      {String(index).padStart(2, "0")}
                    </span>
                  )}
                  <span className="text-mono-label">{label}</span>
                </div>
              )}
              {title && (
                <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.02] text-neutral-100 max-w-3xl">
                  {title}
                </h2>
              )}
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
        >
          {children}
        </motion.div>
      </div>
    </section>
  );
};

export default Section;
